import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'
import { Loader2, DollarSign, FileText, Settings, Check, Sparkles } from 'lucide-react'
import { useSkills } from '@/hooks/skills'
import { useCreateProject } from '@/hooks/projects'
import { createProjectSchema } from '@/lib/validations'
import type { CreateProjectFormData } from '@/lib/validations'
import { Button, Input, Textarea, Label, Badge, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'

export default function Post() {
  const { data: skills, isLoading: skillsLoading } = useSkills()
  const createProject = useCreateProject()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      skillIds: [],
    },
  })

  const selectedSkillIds = watch('skillIds') || []
  const titleValue = watch('title')
  const descriptionValue = watch('description')

  const toggleSkill = (skillId: number) => {
    const current = selectedSkillIds
    const updated = current.includes(skillId)
      ? current.filter((id) => id !== skillId)
      : [...current, skillId]
    setValue('skillIds', updated)
  }

  const onSubmit = async (data: CreateProjectFormData) => {
    await createProject.mutateAsync({
      title: data.title,
      description: data.description,
      skill_ids: data.skillIds,
      budget_min: data.budgetMin,
      budget_max: data.budgetMax,
    })
    reset()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50">
      <div className="container mx-auto max-w-4xl p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 py-12 text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" />
            Create Your Project
          </div>
          <h1 className="text-4xl font-bold">Post a Project</h1>
          <p className="mx-auto max-w-2xl text-xl text-muted-foreground">
            Tell us about your vision and connect with talented developers who can bring it to life.
          </p>
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="border-0 bg-card/80 shadow-warm-lg backdrop-blur-sm">
              <CardHeader className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Project Details</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Provide the essential information about your project
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                  {/* Title */}
                  <div className="space-y-3">
                    <Label htmlFor="title" className="text-base font-medium">
                      Project Title *
                    </Label>
                    <Input
                      id="title"
                      placeholder="e.g., Build a React dashboard with real-time analytics"
                      {...register('title')}
                      className={`h-12 text-base ${errors.title ? 'border-destructive focus-visible:ring-destructive' : 'focus-visible:ring-primary'}`}
                    />
                    {errors.title && (
                      <p className="flex items-center gap-1 text-sm text-destructive">
                        <span>⚠️</span> {errors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-3">
                    <Label htmlFor="description" className="text-base font-medium">
                      Project Description *
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Describe your project in detail. What features do you need? What's the scope? Any specific requirements or preferences?"
                      rows={6}
                      {...register('description')}
                      className={`resize-none text-base ${errors.description ? 'border-destructive focus-visible:ring-destructive' : 'focus-visible:ring-primary'}`}
                    />
                    <div className="flex items-center justify-between">
                      {errors.description ? (
                        <p className="flex items-center gap-1 text-sm text-destructive">
                          <span>⚠️</span> {errors.description.message}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {descriptionValue?.length || 0} characters (minimum 10)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-primary" />
                      <Label className="text-base font-medium">Budget Range (Optional)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="budgetMin" className="text-sm text-muted-foreground">
                          Minimum ($)
                        </Label>
                        <Input
                          id="budgetMin"
                          type="number"
                          placeholder="2,000"
                          {...register('budgetMin', { valueAsNumber: true })}
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budgetMax" className="text-sm text-muted-foreground">
                          Maximum ($)
                        </Label>
                        <Input
                          id="budgetMax"
                          type="number"
                          placeholder="8,000"
                          {...register('budgetMax', { valueAsNumber: true })}
                          className="h-11"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Settings className="h-5 w-5 text-primary" />
                        <Label className="text-base font-medium">Required Skills *</Label>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {selectedSkillIds.length} selected
                      </div>
                    </div>

                    {skillsLoading ? (
                      <div className="flex items-center justify-center space-y-2 py-8">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          <span className="text-muted-foreground">Loading skills...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {skills?.map((skill) => {
                            const isSelected = selectedSkillIds.includes(skill.id)
                            return (
                              <Badge
                                key={skill.id}
                                variant={isSelected ? 'default' : 'outline'}
                                className={`cursor-pointer justify-center px-3 py-2 transition-all duration-200 hover:scale-105 ${
                                  isSelected
                                    ? 'bg-primary text-primary-foreground shadow-lg'
                                    : 'hover:border-primary/50 hover:bg-primary/10'
                                }`}
                                onClick={() => toggleSkill(skill.id)}
                              >
                                {isSelected && <Check className="mr-1 h-3 w-3" />}
                                {skill.name}
                              </Badge>
                            )
                          })}
                        </div>
                        {errors.skillIds && (
                          <p className="flex items-center gap-1 text-sm text-destructive">
                            <span>⚠️</span> {errors.skillIds.message}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="group relative h-12 w-full overflow-hidden text-base font-medium"
                    disabled={isSubmitting || createProject.isPending}
                  >
                    {isSubmitting || createProject.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Posting Your Project...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                        Post Project
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Progress Card */}
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Check className="h-5 w-5 text-primary" />
                  Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div
                    className={`flex items-center gap-2 ${titleValue ? 'text-primary' : 'text-muted-foreground'}`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${titleValue ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    />
                    <span className="text-sm">Project title</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${descriptionValue?.length >= 10 ? 'text-primary' : 'text-muted-foreground'}`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${descriptionValue?.length >= 10 ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    />
                    <span className="text-sm">Detailed description</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${selectedSkillIds.length > 0 ? 'text-primary' : 'text-muted-foreground'}`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${selectedSkillIds.length > 0 ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    />
                    <span className="text-sm">Required skills</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tips Card */}
            <Card className="shadow-warm-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  Pro Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="flex gap-2">
                  <span>💡</span>
                  <span>Be specific about your requirements to attract the right developers</span>
                </div>
                <div className="flex gap-2">
                  <span>🎯</span>
                  <span>Include examples or references when possible</span>
                </div>
                <div className="flex gap-2">
                  <span>💰</span>
                  <span>Fair budget ranges help you get quality proposals</span>
                </div>
                <div className="flex gap-2">
                  <span>⚡</span>
                  <span>Clear timelines set proper expectations</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
